import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell, BellOff, Check, ChevronRight, Settings, Sparkles,
  ClipboardList, CreditCard, Database, Mail, Server, ShieldCheck, Siren, X,
  Puzzle, Rocket, Wallet, TrendingUp, Users, Wifi, WifiOff, Lock,
} from "lucide-react";

/**
 * Owner Notification Panel — dropdown anchored to the top-bar bell.
 * Design language identical to the rest of the Owner Console.
 */

/* ---- category metadata --------------------------------------------------- */
const CATEGORY_META = {
  platform:       { label: "Platform",       icon: Sparkles,      tone: "brand" },
  billing:        { label: "Billing",        icon: Wallet,        tone: "warning" },
  security:       { label: "Security",       icon: ShieldCheck,   tone: "danger" },
  communications: { label: "Communications", icon: Mail,          tone: "info" },
  infrastructure: { label: "Infrastructure", icon: Server,        tone: "success" },
};

/* ---- lucide icon name → component map (matches strings emitted by backend) */
const ICONS = {
  Bell, BellOff, Sparkles, ClipboardList, CreditCard, Database, Mail, Server,
  ShieldCheck, Siren, Puzzle, Rocket, Wallet, TrendingUp, Users, Wifi, WifiOff, Lock,
};
function iconFor(name) {
  return ICONS[name] || Bell;
}

/* ---- humanised timestamp ------------------------------------------------- */
function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now  = Date.now();
  const secs = Math.max(1, Math.round((now - then) / 1000));
  if (secs < 60)     return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60)     return `${mins}m ago`;
  const hrs  = Math.round(mins / 60);
  if (hrs  < 24)     return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 7)      return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/* -------------------------------------------------------------------------- */
export default function NotificationPanel({
  open, anchorRef, onClose,
  unreadTotal = 0, unreadByCategory = {}, groups = {}, categories = [],
  loading = false, error = null,
  onMarkRead, onMarkAllRead, onDismiss, onRefresh,
}) {
  const nav = useNavigate();
  const panelRef = useRef(null);
  const [activeCat, setActiveCat] = useState("all");
  const [showSettings, setShowSettings] = useState(false);

  // Load fresh data when opened.
  useEffect(() => {
    if (open) {
      setShowSettings(false);
      onRefresh?.();
    }
  }, [open, onRefresh]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, anchorRef, onClose]);

  const catList = useMemo(() => {
    if (categories.length) return categories;
    return Object.keys(CATEGORY_META);
  }, [categories]);

  const visibleGroups = useMemo(() => {
    if (activeCat === "all") return catList.map(c => [c, groups[c] || []]);
    return [[activeCat, groups[activeCat] || []]];
  }, [activeCat, catList, groups]);

  const openNotification = async (n) => {
    if (!n.read) await onMarkRead?.(n.id);
    if (n.action_url) {
      onClose?.();
      nav(n.action_url);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={panelRef}
          initial={{ opacity: 0, y: -6, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.98 }}
          transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
          className="hbf-notif-panel"
          data-testid="owner-notification-panel"
          role="dialog"
          aria-label="Notifications"
        >
          {/* -------- header -------------------------------------------- */}
          <div className="hbf-notif-panel__header">
            <div className="hbf-notif-panel__title">
              <Bell size={14} strokeWidth={2.2} />
              <span>Notifications</span>
              {unreadTotal > 0 && (
                <span className="hbf-notif-panel__count" data-testid="notif-panel-count">
                  {unreadTotal}
                </span>
              )}
            </div>
            <div className="hbf-notif-panel__header-actions">
              <button
                type="button" className="hbf-notif-panel__link"
                onClick={() => setShowSettings(s => !s)}
                data-testid="notif-settings-toggle"
                title="Notification settings"
              >
                <Settings size={13} strokeWidth={2} />
              </button>
              <button
                type="button" className="hbf-notif-panel__link"
                onClick={() => { onClose?.(); nav("/owner/notifications"); }}
                data-testid="notif-view-all"
                title="Open full notifications page"
              >
                <ChevronRight size={14} strokeWidth={2} />
              </button>
            </div>
          </div>

          {showSettings ? (
            <NotificationSettingsInline onDone={() => setShowSettings(false)} />
          ) : (
            <>
              {/* -------- category tabs ---------------------------------- */}
              <div className="hbf-notif-panel__tabs" role="tablist">
                <button
                  type="button" role="tab"
                  className={`hbf-notif-panel__tab ${activeCat === "all" ? "is-active" : ""}`}
                  onClick={() => setActiveCat("all")}
                  data-testid="notif-tab-all"
                >
                  All{unreadTotal > 0 ? ` (${unreadTotal})` : ""}
                </button>
                {catList.map(c => {
                  const meta = CATEGORY_META[c] || {};
                  const n = unreadByCategory[c] || 0;
                  return (
                    <button
                      key={c} type="button" role="tab"
                      className={`hbf-notif-panel__tab ${activeCat === c ? "is-active" : ""}`}
                      onClick={() => setActiveCat(c)}
                      data-testid={`notif-tab-${c}`}
                    >
                      {meta.label || c}{n > 0 ? ` (${n})` : ""}
                    </button>
                  );
                })}
              </div>

              {/* -------- body ------------------------------------------- */}
              <div className="hbf-notif-panel__body" data-testid="notif-panel-body">
                {error && (
                  <div className="hbf-notif-panel__error">Failed to load notifications: {error}</div>
                )}
                {loading && !visibleGroups.some(([, arr]) => arr.length) ? (
                  <NotifSkeleton />
                ) : (
                  visibleGroups.map(([cat, list]) => (
                    <NotifGroup
                      key={cat}
                      category={cat}
                      list={list}
                      activeCat={activeCat}
                      onOpen={openNotification}
                      onDismiss={onDismiss}
                    />
                  ))
                )}
                {!loading && visibleGroups.every(([, arr]) => arr.length === 0) && (
                  <div className="hbf-notif-panel__empty">
                    <BellOff size={22} strokeWidth={1.6} />
                    <div className="hbf-notif-panel__empty-title">You&apos;re all caught up</div>
                    <div className="hbf-notif-panel__empty-desc">
                      Signals from Stripe, Twilio, Resend, R2, Readiness and the SOC will appear
                      here as they happen.
                    </div>
                  </div>
                )}
              </div>

              {/* -------- footer ----------------------------------------- */}
              <div className="hbf-notif-panel__footer">
                <button
                  type="button" className="hbf-notif-panel__footer-btn"
                  onClick={() => onMarkAllRead?.(activeCat === "all" ? null : activeCat)}
                  disabled={unreadTotal === 0}
                  data-testid="notif-mark-all-read"
                >
                  <Check size={13} strokeWidth={2.2} /> Mark all as read
                </button>
                <button
                  type="button" className="hbf-notif-panel__footer-btn is-primary"
                  onClick={() => { onClose?.(); nav("/owner/notifications"); }}
                  data-testid="notif-open-all"
                >
                  View all notifications
                </button>
              </div>
            </>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* -------------------------------------------------------------------------- */
function NotifGroup({ category, list, activeCat, onOpen, onDismiss }) {
  const meta = CATEGORY_META[category] || {};
  const Icon = meta.icon || Bell;
  // In per-category mode we hide the sub-header entirely (feels redundant).
  const hideHeader = activeCat !== "all";
  if (!list?.length && hideHeader) {
    return (
      <div className="hbf-notif-panel__empty" style={{ padding: "24px 20px" }}>
        <div className="hbf-notif-panel__empty-title">Nothing in {meta.label || category}</div>
      </div>
    );
  }
  if (!list?.length) return null;
  return (
    <div className="hbf-notif-group" data-testid={`notif-group-${category}`}>
      {!hideHeader && (
        <div className={`hbf-notif-group__header hbf-notif-group__header--${meta.tone || "brand"}`}>
          <span className="hbf-notif-group__icon"><Icon size={11} strokeWidth={2.2} /></span>
          <span className="hbf-notif-group__label">{meta.label || category}</span>
          <span className="hbf-notif-group__count">{list.length}</span>
        </div>
      )}
      <ul className="hbf-notif-group__items" role="list">
        {list.map(n => (
          <NotifRow key={n.id} n={n} onOpen={onOpen} onDismiss={onDismiss} />
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
function NotifRow({ n, onOpen, onDismiss }) {
  const Icon = iconFor(n.icon);
  const tone = n.level || "info";
  return (
    <li
      className={`hbf-notif-row hbf-notif-row--${tone} ${n.read ? "is-read" : ""}`}
      data-testid={`notif-row-${n.id}`}
    >
      <button
        type="button" className="hbf-notif-row__main"
        onClick={() => onOpen(n)}
        data-testid={`notif-row-open-${n.id}`}
      >
        <span className={`hbf-notif-row__icon hbf-notif-row__icon--${tone}`}>
          <Icon size={14} strokeWidth={2.2} />
        </span>
        <span className="hbf-notif-row__body">
          <span className="hbf-notif-row__title">
            {!n.read && <span className="hbf-notif-row__unread-dot" aria-hidden />}
            {n.title}
          </span>
          {n.description && (
            <span className="hbf-notif-row__desc">{n.description}</span>
          )}
          <span className="hbf-notif-row__meta">
            <span className="hbf-notif-row__ts">{relTime(n.created_at)}</span>
            {n.source && <span className="hbf-notif-row__source">· {n.source}</span>}
            {n.action_url && (
              <span className="hbf-notif-row__cta">
                {n.action_label || "Open"} <ChevronRight size={11} strokeWidth={2.4} />
              </span>
            )}
          </span>
        </span>
      </button>
      <button
        type="button" className="hbf-notif-row__dismiss"
        onClick={(e) => { e.stopPropagation(); onDismiss?.(n.id); }}
        aria-label="Dismiss notification"
        data-testid={`notif-row-dismiss-${n.id}`}
        title="Dismiss"
      >
        <X size={12} strokeWidth={2} />
      </button>
    </li>
  );
}

/* -------------------------------------------------------------------------- */
function NotifSkeleton() {
  return (
    <div className="hbf-notif-panel__skel" aria-hidden>
      {[0, 1, 2].map(i => (
        <div key={i} className="hbf-notif-panel__skel-row">
          <div className="hbf-notif-panel__skel-icon" />
          <div className="hbf-notif-panel__skel-lines">
            <div className="hbf-notif-panel__skel-line hbf-skel" style={{ width: "60%" }} />
            <div className="hbf-notif-panel__skel-line hbf-skel" style={{ width: "90%" }} />
            <div className="hbf-notif-panel__skel-line hbf-skel" style={{ width: "40%" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/**
 * Inline preferences UI. Reads/writes /api/owner/notifications/prefs.
 */
function NotificationSettingsInline({ onDone }) {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    let alive = true;
    import("@/lib/api").then(({ default: api }) => {
      api.get("/owner/notifications/prefs")
        .then(({ data }) => { if (alive) setPrefs(data); })
        .catch(e => { if (alive) setErr(e?.message || "load failed"); });
    });
    return () => { alive = false; };
  }, []);

  const patch = async (next) => {
    setSaving(true); setErr(null);
    try {
      const { default: api } = await import("@/lib/api");
      const { data } = await api.patch("/owner/notifications/prefs", next);
      setPrefs(data);
    } catch (e) {
      setErr(e?.message || "save failed");
    } finally { setSaving(false); }
  };

  const toggleCat = (cat) => {
    const cur = new Set(prefs?.muted_categories || []);
    if (cur.has(cat)) cur.delete(cat); else cur.add(cat);
    patch({ muted_categories: Array.from(cur) });
  };

  return (
    <div className="hbf-notif-settings" data-testid="notif-settings-panel">
      <div className="hbf-notif-settings__section">
        <div className="hbf-eyebrow">MUTE CATEGORIES</div>
        <div className="hbf-notif-settings__cats">
          {Object.entries(CATEGORY_META).map(([cat, meta]) => {
            const Icon = meta.icon;
            const muted = (prefs?.muted_categories || []).includes(cat);
            return (
              <button
                key={cat} type="button"
                onClick={() => toggleCat(cat)}
                className={`hbf-notif-settings__cat ${muted ? "is-muted" : ""}`}
                data-testid={`notif-settings-mute-${cat}`}
                disabled={saving || !prefs}
              >
                <Icon size={13} strokeWidth={2} />
                <span>{meta.label}</span>
                <span className="hbf-notif-settings__cat-state">
                  {muted ? "Muted" : "On"}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="hbf-notif-settings__section">
        <div className="hbf-eyebrow">DELIVERY</div>
        <SettingToggle
          label="Email digest"
          description="Receive a daily summary of unread notifications."
          checked={!!prefs?.email_digest}
          onChange={(v) => patch({ email_digest: v })}
          disabled={saving || !prefs}
          testId="notif-settings-email-digest"
        />
        <SettingToggle
          label="Sound"
          description="Soft chime when a new notification arrives."
          checked={!!prefs?.sound}
          onChange={(v) => patch({ sound: v })}
          disabled={saving || !prefs}
          testId="notif-settings-sound"
        />
      </div>

      {err && <div className="hbf-notif-panel__error">{err}</div>}

      <div className="hbf-notif-settings__footer">
        <button
          type="button" className="hbf-notif-panel__footer-btn"
          onClick={onDone}
          data-testid="notif-settings-back"
        >
          Back
        </button>
        <button
          type="button" className="hbf-notif-panel__footer-btn is-primary"
          onClick={() => nav("/owner/settings")}
          data-testid="notif-settings-open-full"
        >
          Open full settings
        </button>
      </div>
    </div>
  );
}

function SettingToggle({ label, description, checked, onChange, disabled, testId }) {
  return (
    <div className="hbf-notif-settings__row">
      <div className="hbf-notif-settings__row-body">
        <div className="hbf-notif-settings__row-title">{label}</div>
        {description && <div className="hbf-notif-settings__row-desc">{description}</div>}
      </div>
      <button
        type="button" role="switch" aria-checked={checked}
        className={`hbf-switch ${checked ? "is-on" : ""}`}
        onClick={() => onChange(!checked)}
        disabled={disabled}
        data-testid={testId}
      >
        <span className="hbf-switch__thumb" />
      </button>
    </div>
  );
}
